import { useEffect, useState } from "react";
import { api, ApiError } from "./api.js";
import { device } from "./device.js";

// Stav identity zařízení (REQ-032): `off` bez identity v instanci (API 404, diagnostika, bez
// e-mailu), `anon` nepřihlášeno, `in` přihlášeno. Jeden dotaz pro celou aplikaci, po přihlášení
// a odhlášení se obnoví.

export type IdentityState = { readonly kind: "loading" } | { readonly kind: "off" } | { readonly kind: "anon" } | { readonly kind: "in"; readonly email: string };

let current: IdentityState = { kind: "loading" };
let pending: Promise<void> | null = null;
const listeners = new Set<(s: IdentityState) => void>();

function set(next: IdentityState): void {
  current = next;
  for (const l of listeners) l(next);
}

/** Načte stav identity znovu (po přihlášení, odhlášení). */
export function refreshIdentity(): Promise<void> {
  pending = api.identity().then(
    (r) => set({ kind: "in", email: r.email }),
    (e: unknown) => set(e instanceof ApiError && e.status === 404 ? { kind: "off" } : { kind: "anon" }),
  );
  return pending;
}

export function useIdentity(): IdentityState {
  const [state, setState] = useState(current);
  useEffect(() => {
    listeners.add(setState);
    if (pending === null) void refreshIdentity();
    else setState(current);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}

/** Připojí sezení uložená v zařízení k přihlášené identitě (REQ-032). Chyba nevadí, zkusí se příště. */
export async function attachDeviceSessions(): Promise<void> {
  const mine = (await device.sessions()).filter((s) => s.origin === "user");
  if (mine.length === 0) return;
  const ok = await api.identityAttach(mine.map((s) => ({ id: s.id, token: s.token }))).then(
    () => true,
    () => false,
  );
  if (ok) for (const s of mine) if (s.saved !== true) await device.saveSession({ ...s, saved: true });
}
